import { translation, useSelector, formatToReactComponent, TranslatedComponent } from '@hh.ru/front-static-app';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import Column, { ColumnsRow } from 'bloko/blocks/column';
import BlokoLink, { LinkAppearance } from 'bloko/blocks/link';
import Text, { TextImportance, TextSize } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import useToggleState from 'src/hooks/useToggleState';
import { COMPLETE_RESUME_PACKAGES } from 'src/types/applicantServices';

import styles from './index.less';

const TrlKeys = {
    titleFinishResume: 'applicantServices.workDone.finishResume.title',
    titleFinishConsult: 'applicantServices.workDone.finishConsult.title',
    titleFinishInterview: 'applicantServices.workDone.finishInterview.title',
    linkInformation: 'applicantServices.workDone.linkInformation.text',
    resumeAbout: 'applicantServices.workDone.resumeAbout',
    consultationAbout: 'applicantServices.workDone.consultationAbout',
    textMaterials: 'applicantServices.workDone.aboutMaterials.text',
    linkMaterials: 'applicantServices.workDone.aboutMaterials.link',
    changeResumeVisibility: 'applicantServices.workDone.resumeVisibility.text',
    changeResumeVisibilityLink: 'applicantServices.workDone.resumeVisibility.link',
};

const Features = {
    showVisibilityResumeBlock: 'show_visibility_resume_block',
};

const WorkDone: TranslatedComponent = ({ trls }) => {
    const order = useSelectorNonNullable(({ careerServiceOrder }) => careerServiceOrder);
    const resumes = useSelectorNonNullable((state) => state.resumeIdentifiers);
    const selectedResume = resumes.filter((resume) => resume.hash === order.resumeHash)[0];
    const showVisibilityResumeBlock = useSelector(({ features }) => features[Features.showVisibilityResumeBlock]);
    const [isShowInformation, toggleIsShowInformation] = useToggleState(false);
    const canShowResume = order.targetResumeHash && order.targetResumeTitle;

    return (
        <Column xs="4" s="6" m="9" l="11" container>
            {(order.packageType === COMPLETE_RESUME_PACKAGES.BASE ||
                order.packageType === COMPLETE_RESUME_PACKAGES.OPTIMAL ||
                order.packageType === COMPLETE_RESUME_PACKAGES.MAXIMAL) && (
                <>
                    <ColumnsRow>
                        <div className={styles.containerInfo}>
                            <Column xs="4" s="6" m="3" l="4">
                                <Text size={TextSize.ExtraLarge}>{trls[TrlKeys.titleFinishResume]}</Text>
                            </Column>
                            <Column xs="4" s="6" m="6" l="7">
                                {canShowResume && (
                                    <Text>
                                        <BlokoLink
                                            Element={SPALink}
                                            to={`/resume/${order.targetResumeHash}`}
                                            target="_blank"
                                            disableVisited
                                            data-qa="expert-service-resume"
                                        >
                                            {order.targetResumeTitle}
                                        </BlokoLink>
                                    </Text>
                                )}
                            </Column>
                        </div>
                    </ColumnsRow>
                    {canShowResume && showVisibilityResumeBlock && (
                        <ColumnsRow>
                            <Column xs="4" s="8" m="12" l="12">
                                <VSpacing base={4} />
                                <Text>
                                    {formatToReactComponent(trls[TrlKeys.changeResumeVisibility], {
                                        '{0}': (
                                            <SPALink
                                                target="_blank"
                                                to={`/applicant/resumes/edit/visibility?resume=${order.targetResumeHash}`}
                                            >
                                                {trls[TrlKeys.changeResumeVisibilityLink]}
                                            </SPALink>
                                        ),
                                    })}
                                </Text>
                            </Column>
                        </ColumnsRow>
                    )}
                </>
            )}
            {(order.packageType === COMPLETE_RESUME_PACKAGES.CONSULT_ONLY ||
                order.packageType === COMPLETE_RESUME_PACKAGES.INTERVIEW_PRACTICE) && (
                <>
                    <ColumnsRow>
                        <Column xs="4" s="6" m="9" l="11">
                            <Text size={TextSize.ExtraLarge}>
                                {order.packageType === COMPLETE_RESUME_PACKAGES.CONSULT_ONLY
                                    ? trls[TrlKeys.titleFinishConsult]
                                    : trls[TrlKeys.titleFinishInterview]}
                            </Text>
                        </Column>
                    </ColumnsRow>
                    {order.packageType === COMPLETE_RESUME_PACKAGES.INTERVIEW_PRACTICE && (
                        <>
                            <VSpacing base={2} />
                            <ColumnsRow>
                                <Column xs="4" s="6" m="9" l="11">
                                    <Text>
                                        {trls[TrlKeys.textMaterials]}{' '}
                                        <BlokoLink
                                            Element={SPALink}
                                            to="/interview_practice_guide"
                                            target="_blank"
                                            disableVisited
                                        >
                                            {trls[TrlKeys.linkMaterials]}
                                        </BlokoLink>
                                    </Text>
                                </Column>
                            </ColumnsRow>
                        </>
                    )}
                    <VSpacing base={6} />
                    <div className={styles.separator} />
                    <VSpacing base={6} />
                    <ColumnsRow>
                        <Column xs="4" s="6" m="9" l="11">
                            <BlokoLink appearance={LinkAppearance.Pseudo} onClick={toggleIsShowInformation}>
                                {trls[TrlKeys.linkInformation]}
                            </BlokoLink>
                        </Column>
                    </ColumnsRow>
                    <VSpacing base={6} />
                    {isShowInformation && (
                        <>
                            <ColumnsRow>
                                <Column xs="4" s="6" m="3" l="4">
                                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.resumeAbout]}</Text>
                                </Column>
                                <Column xs="4" s="6" m="6" l="7">
                                    {selectedResume && (
                                        <Text>
                                            <BlokoLink
                                                Element={SPALink}
                                                to={`/resume/${selectedResume.hash}`}
                                                target="_blank"
                                                disableVisited
                                            >
                                                {selectedResume.title}
                                            </BlokoLink>
                                        </Text>
                                    )}
                                </Column>
                            </ColumnsRow>
                            <VSpacing base={6} />
                            <ColumnsRow>
                                <Column xs="4" s="6" m="3" l="4">
                                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.consultationAbout]}</Text>
                                </Column>
                                <Column xs="4" s="6" m="6" l="7">
                                    <Text>{order.description}</Text>
                                </Column>
                            </ColumnsRow>
                        </>
                    )}
                </>
            )}
        </Column>
    );
};

export default translation(WorkDone);
